// El texto se renderiza siempre con interpolación de Vue, nunca con v-html.

import { parseBoldSegments as parseRichText, segmentsToPlainText } from '../../server/seo/rich-text';

const DIACRITICS_REGEX = /[\u0300-\u036f]/g;
const LINK_REGEX = /\[([^\]\n]+)\]\(([^()\s]+)\)/g;
const AUTOLINK_REGEX = /<((?:https?:\/\/|mailto:)[^\s<>]+)>/gi;
const LINK_TOKEN_REGEX = /\uE000(\d+)\uE001/g;
const LINK_TOKEN_CHARS_REGEX = /[\uE000\uE001]/g;
const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];

export function normalizeText (text) {
    return (text || '')
        .toString()
        .normalize('NFD')
        .replace(DIACRITICS_REGEX, '')
        .toLowerCase();
}

// El texto lo genera un modelo y `:href` no frena `javascript:`. Los links relativos se resuelven contra el sitio actual.
function safeHref (url) {
    let parsed;
    try {
        parsed = new URL(url.trim(), window.location.origin);
    } catch (e) {
        return null;
    }
    return SAFE_PROTOCOLS.includes(parsed.protocol) ? parsed.href : null;
}

function linkParts ({ label, href }, isBold) {
    return parseRichText(label).map(segment => ({
        text: segment.text,
        isBold: isBold || segment.isBold,
        ...(href && { href }),
    }));
}

function expandLinkTokens (segment, links) {
    const tokenRegex = new RegExp(LINK_TOKEN_REGEX.source, LINK_TOKEN_REGEX.flags);
    const parts = [];
    let cursor = 0;
    let match = tokenRegex.exec(segment.text);

    while (match) {
        if (match.index > cursor) {
            parts.push({ text: segment.text.slice(cursor, match.index), isBold: segment.isBold });
        }
        parts.push(...linkParts(links[match[1]], segment.isBold));
        cursor = match.index + match[0].length;
        match = tokenRegex.exec(segment.text);
    }

    if (cursor < segment.text.length) {
        parts.push({ text: segment.text.slice(cursor), isBold: segment.isBold });
    }

    return parts.length > 0 ? parts : [segment];
}

// Los links se reemplazan por marcadores antes de procesar las negritas, así funcionan tanto
// `[**texto**](url)` como `**[texto](url)**`. Los autolinks `<url>` se pasan a `[url](url)`
// porque si no stripTags los borra como si fueran etiquetas.
function parseWithLinks (text) {
    const links = [];
    const tokenized = (text || '')
        .toString()
        .replace(LINK_TOKEN_CHARS_REGEX, '')
        .replace(AUTOLINK_REGEX, (match, url) => `[${url}](${url})`)
        .replace(LINK_REGEX, (match, label, url) => {
            links.push({ label, href: safeHref(url) });
            return `\uE000${links.length - 1}\uE001`;
        });

    return parseRichText(tokenized).flatMap(segment => expandLinkTokens(segment, links));
}

// `links` va apagado por defecto: en el contenido del Google Sheet un corchete suelto no debe volverse link.
export function parseBoldSegments (text, options = {}) {
    return options.links ? parseWithLinks(text) : parseRichText(text);
}

// Normalizar puede cambiar la longitud del texto: los rangos se mapean índice a índice al texto original.
function findMatchRanges (text, normalizedQuery) {
    if (!text || !normalizedQuery) return [];

    let normalizedText = '';
    const normalizedToOriginalIndex = [];

    for (let i = 0; i < text.length; i += 1) {
        for (const char of normalizeText(text[i])) {
            normalizedText += char;
            normalizedToOriginalIndex.push(i);
        }
    }

    const ranges = [];
    let fromIndex = 0;

    while (fromIndex <= normalizedText.length - normalizedQuery.length) {
        const start = normalizedText.indexOf(normalizedQuery, fromIndex);
        if (start === -1) break;

        const end = start + normalizedQuery.length - 1;
        const originalStart = normalizedToOriginalIndex[start];
        const originalEnd = normalizedToOriginalIndex[end] + 1;
        const last = ranges[ranges.length - 1];

        if (last && originalStart <= last[1]) {
            last[1] = Math.max(last[1], originalEnd);
        } else {
            ranges.push([originalStart, originalEnd]);
        }

        fromIndex = start + normalizedQuery.length;
    }

    return ranges;
}

// Los rangos se calculan sobre el texto completo para resaltar también coincidencias que cruzan una negrita.
export function highlightSegments (segments, normalizedQuery) {
    if (!normalizedQuery) {
        return segments.map(segment => ({ ...segment, isMatch: false }));
    }

    const fullText = segmentsToPlainText(segments);
    const ranges = findMatchRanges(fullText, normalizedQuery);

    if (ranges.length === 0) {
        return segments.map(segment => ({ ...segment, isMatch: false }));
    }

    const parts = [];
    let segmentStart = 0;

    segments.forEach(segment => {
        const segmentEnd = segmentStart + segment.text.length;
        let cursor = segmentStart;

        ranges.forEach(([start, end]) => {
            if (end <= segmentStart || start >= segmentEnd) return;

            const matchStart = Math.max(start, segmentStart);
            const matchEnd = Math.min(end, segmentEnd);

            if (cursor < matchStart) {
                parts.push({ text: fullText.slice(cursor, matchStart), isBold: segment.isBold, isMatch: false });
            }

            parts.push({ text: fullText.slice(matchStart, matchEnd), isBold: segment.isBold, isMatch: true });
            cursor = matchEnd;
        });

        if (cursor < segmentEnd) {
            parts.push({ text: fullText.slice(cursor, segmentEnd), isBold: segment.isBold, isMatch: false });
        }

        segmentStart = segmentEnd;
    });

    return parts.length > 0 ? parts : [{ text: fullText, isBold: false, isMatch: false }];
}

export function prepareFaq (faq) {
    const questionSegments = parseBoldSegments(faq.question);
    const answerSegments = parseBoldSegments(faq.answer);
    const question = segmentsToPlainText(questionSegments);
    const answer = segmentsToPlainText(answerSegments);

    return {
        question,
        answer,
        questionSegments,
        answerSegments,
        searchText: normalizeText(`${question} ${answer}`),
    };
}
